"use client";

import React, { useEffect, useCallback, useState, useMemo } from "react";
import moment from "moment-timezone";
import { useCheckoutBase, defaultGuest } from "../hooks/use-checkout-base";
import { CheckoutPanel } from "../checkout-panel";
import { CheckoutFooter } from "../checkout-footer";
import { CheckoutSummaryCard } from "../checkout-summary-card";
import { CalendarVisibilityStep } from "../calendar-visibility-step";
import type { ListingInfo } from "../calendar-visibility-step";
import { AddOnsStep } from "../add-ons-step";
import { UserFormStep } from "../user-form-step";
import { PersonalDetailsStep } from "../personal-details-step";
import { hasRenderableQuestions } from "../utils/guest-questions";
import { validateGuest, hasGuestErrors } from "../validation";
import { BasketChoiceStep, type BasketChoiceNotice } from "../basket-choice-step";
import { useV2Pricing } from "../hooks/use-v2-pricing";
import { TicketSelectionStepV2 } from "../ticket-selection-step-v2";
import {
  appointmentCapTicketTypeId,
  appointmentPoolCaps,

  isVariableDurationCourse,
} from "../../../lib/checkout/pool-availability";
import type { CustomerBookingHistory } from "../../../lib/checkout/types";
import type {
  DiscountResult,
  TicketSelection,
} from "../utils/types";
import type { AvailableDate } from "../appointment-time-slot-step";

type CheckoutStep = "calendar" | "tickets" | "addons" | "userform" | "personal" | "basketChoice";

export type CheckoutCalendarVisibilityProps = {
  listings: ListingInfo[];
  coursesData: Map<string, any>;
  ticketTypesMap: Map<string, any[]>;
  ticketTypesV2Map: Map<string, any[]>;
  /** Per-listing IDs of ticket types the current buyer has already purchased
   *  — drives locked/hidden display state for prior-purchase-gated V2
   *  tickets. Keyed the same way as `ticketTypesV2Map`. */
  priorPurchasedTicketIdsMap?: Map<string, string[]>;
  /** Per-listing clamped booking counts for the current buyer — feeds the
   *  customer-history pricing conditions in the price preview. Keyed the same
   *  way as `ticketTypesV2Map`. */
  customerBookingHistoryMap?: Map<string, CustomerBookingHistory>;
  addOnsMap: Map<string, any[]>;

  timezone?: string;
  currency?: string;

  onLoadAvailableSlots: (
    listingId: string,
    month: number,
    year: number,
    /**
     * Names the ticket type whose resource-pool cap the server should compute
     * for each slot. Optional: without it the response simply carries no cap,
     * which is what this surface got before — the parameter did not exist, so
     * the server's pool block could never run here at all.
     */
    ticketTypeId?: string,
  ) => Promise<{ dates: AvailableDate[]; slots: any[] }>;
  onConfirmFreeBooking?: (data: any) => Promise<void>;
  onValidateDiscount?: (code: string, email: string) => Promise<DiscountResult>;
  onSuccess?: (purchase: any) => void;
  onError?: (error: Error | string) => void;
  onClose?: () => void;
  onStepChange?: (step: string) => void;
  /** Called when the checkout phase begins (user completed calendar selection) */
  onCheckoutStart?: () => void;
  /** Called when user goes back from checkout to calendar */
  onCheckoutEnd?: () => void;

  cancellationPolicyUrl?: string;
  termsSettings?: import("../utils/types").TermsSettings;
  customTerms?: import("../utils/types").CustomTerms;
  onOpenTermsDialog?: (data: {
    termsSettings?: import("../utils/types").TermsSettings;
    customTerms?: import("../utils/types").CustomTerms;
    timezone?: string;
  }) => void;

  school?: {
    name?: string;
    _id?: string;
    isNewsletterSignupEnabled?: boolean;
    customNewsletterSignupText?: string | null;
    businessCountry?: string;
  };
  mandatoryTickBox?: boolean;
  isEmbedMode?: boolean;
  basketItemCount?: number;
  onAddToBasket?: (itemData: any) => void;
  onProceedToBasketCheckout: (itemData: any) => void;
  /** When true, skips the "Add to basket vs Checkout" UI and auto-proceeds to basket checkout */
  directCheckout?: boolean;
  /** Why the host refused the last basket choice; shown on the basket choice step. */
  basketConflict?: BasketChoiceNotice | null;
  onLogin?: () => void;
};

export function CheckoutCalendarVisibility({
  listings,
  coursesData,
  ticketTypesMap,
  ticketTypesV2Map,
  priorPurchasedTicketIdsMap,
  customerBookingHistoryMap,
  addOnsMap,
  onLoadAvailableSlots,
  onClose,
  onStepChange,
  onCheckoutStart,
  onCheckoutEnd,
  currency = "GBP",
  timezone = "Europe/London",
  cancellationPolicyUrl = "/terms",
  termsSettings,
  customTerms,
  onOpenTermsDialog,
  mandatoryTickBox = false,
  isEmbedMode = false,
  school,
  basketItemCount = 0,
  onAddToBasket,
  onProceedToBasketCheckout,
  directCheckout = false,
  basketConflict = null,
}: CheckoutCalendarVisibilityProps) {

  // Calendar selection state
  const [step, setStep] = useState<CheckoutStep>("calendar");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<number | null>(null);
  const [selectedListingId, setSelectedListingId] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [availabilityMap, setAvailabilityMap] = useState<Map<string, AvailableDate[]>>(new Map());
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [nextAvailableDateInfo, setNextAvailableDateInfo] = useState<{
    dateISO: string;
    month: number;
    year: number;
  } | null>(null);
  const [isSearchingNextAvailable, setIsSearchingNextAvailable] = useState(false);

  // V2 ticket selections
  const [ticketSelections, setTicketSelections] = useState<TicketSelection[]>([]);

  const updateTicketSelection = useCallback(
    (ticketTypeId: string, quantity: number) => {
      setTicketSelections((prev) => {
        if (quantity === 0) return prev.filter((s) => s.ticketTypeId !== ticketTypeId);
        const existing = prev.find((s) => s.ticketTypeId === ticketTypeId);
        if (existing) return prev.map((s) => s.ticketTypeId === ticketTypeId ? { ...s, quantity } : s);
        return [...prev, { ticketTypeId, quantity }];
      });
    },
    [],
  );

  // Guest validation state
  const [guestTouched, setGuestTouched] = useState<
    Array<Partial<Record<string, boolean>>>
  >([]);

  // Selected course data
  const selectedCourse = selectedListingId ? coursesData.get(selectedListingId) : null;
  const selectedTicketTypes = selectedListingId ? ticketTypesMap.get(selectedListingId) || [] : [];
  const selectedTicketTypesV2 = selectedListingId ? ticketTypesV2Map.get(selectedListingId) || [] : [];
  const selectedPriorPurchasedTicketIds = selectedListingId
    ? priorPurchasedTicketIdsMap?.get(selectedListingId) || []
    : [];
  const selectedCustomerBookingHistory = selectedListingId
    ? customerBookingHistoryMap?.get(selectedListingId)
    : undefined;
  const selectedAddOns = selectedListingId ? addOnsMap.get(selectedListingId) || [] : [];
  const isV2 = selectedCourse?.ticketVersion === "v2";

  // Reset ticket selections when listing changes
  useEffect(() => {
    setTicketSelections([]);
  }, [selectedListingId]);

  const base = useCheckoutBase({
    enabledAddOns: selectedAddOns,
  });

  // Map ticket types to pricing options for the selected listing
  const pricingOptions = useMemo(() => {
    if (!selectedCourse || !selectedTicketTypes.length) return [];

    const durationMap = new Map<string, number>();
    const slots = selectedCourse?.appointmentScheduleSlots || [];
    for (const slot of slots) {
      for (const ticket of slot.tickets || []) {
        const duration = ticket.duration;
        if (typeof duration === "number" && duration > 0) {
          for (const ticketTypeId of ticket.ticketTypes || []) {
            if (!durationMap.has(ticketTypeId)) {
              durationMap.set(ticketTypeId, duration);
            }
          }
        }
      }
    }

    return selectedTicketTypes
      .map((tt: any) => ({
        id: tt._id,
        name: tt.name,
        description: tt.description,
        price: tt.price,
        duration: durationMap.get(tt._id) ?? tt.duration ?? 60,
        ticketsIds: tt.ticketsIds || [tt._id],
        numberOfGuestsPerTicket: tt.numberOfGuestsPerTicket ?? 1,
      }))
      .sort((a: any, b: any) => b.price - a.price);
  }, [selectedTicketTypes, selectedCourse?.appointmentScheduleSlots]);

  // Find the matching pricing option for the selected time slot
  const selectedOptionIndex = useMemo(() => {
    if (!selectedListingId || selectedTime === null || !selectedDate || !pricingOptions.length) {
      return null;
    }

    const dates = availabilityMap.get(selectedListingId) || [];
    const selectedDateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;

    const dateData = dates.find((d) => {
      const dStr = moment.tz(d.dateISO, timezone).format("YYYY-MM-DD");
      return dStr === selectedDateStr;
    });

    if (!dateData?.availableSlots) return 0;

    const matchingSlot = dateData.availableSlots.find(
      (s: any) => s.startMinutes === selectedTime,
    );

    if (!matchingSlot) return 0;

    const scheduleSlot = selectedCourse?.appointmentScheduleSlots?.find(
      (s: any) => s._id === matchingSlot.scheduleSlotId,
    );
    if (!scheduleSlot) return 0;

    const ticket = scheduleSlot.tickets?.[matchingSlot.ticketIndex];
    if (!ticket) return 0;

    const ticketTypeIds = ticket.ticketTypes || [];
    const optionIdx = pricingOptions.findIndex(
      (option: any) =>
        ticketTypeIds.includes(option.id) ||
        option.ticketsIds?.includes(ticket._id),
    );

    return optionIdx >= 0 ? optionIdx : 0;
  }, [selectedListingId, selectedTime, selectedDate, pricingOptions, availabilityMap, timezone, selectedCourse]);

  const selectedOption = selectedOptionIndex !== null ? pricingOptions[selectedOptionIndex] : null;

  // V2 pricing
  const syntheticSession = useMemo(() => {
    if (!selectedDate || selectedTime === null) return null;
    const hours = Math.floor(selectedTime / 60);
    const minutes = selectedTime % 60;
    const courseTimezone = selectedCourse?.timezone || timezone;
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const day = selectedDate.getDate();
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")} ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
    const startDateTime = moment.tz(dateStr, courseTimezone).toISOString();
    return { startDateTime };
  }, [selectedDate, selectedTime, selectedCourse?.timezone, timezone]);

  const v2 = useV2Pricing({
    enabled: isV2,
    ticketTypesV2: selectedTicketTypesV2,
    ticketSelections,
    selectedSession: syntheticSession,
    rawSessionTicketTypes: [],
    numberOfSessions: 1,
    courseTimezone: selectedCourse?.timezone || timezone,
    discount: base.discount,
    addOnsTotal: base.addOnsTotal,
    priorPurchasedTicketIds: selectedPriorPurchasedTicketIds,
    customerBookingHistory: selectedCustomerBookingHistory,
  });

  // Calculate total
  const total = useMemo(() => {
    if (isV2) return v2.total;

    const optionPrice = selectedOption?.price || 0;
    const grossTotal = optionPrice + base.addOnsTotal;
    let discountAmount = 0;
    if (base.discount) {
      const { value, type } = base.discount;
      switch (type) {
        case "PERCENTAGE":
          discountAmount = Math.round((grossTotal * Math.min(value, 100)) / 100);
          break;
        case "PRICE":
        default:
          discountAmount = Math.min(grossTotal, value);
          break;
      }
    }
    return Math.max(0, grossTotal - discountAmount);
  }, [isV2, v2.total, selectedOption, base.addOnsTotal, base.discount]);

  const discountAmount = useMemo(() => {
    if (isV2) return v2.discountAmount;

    if (!base.discount) return 0;
    const optionPrice = selectedOption?.price || 0;
    const grossTotal = optionPrice + base.addOnsTotal;
    const { value, type } = base.discount;
    switch (type) {
      case "PERCENTAGE":
        return Math.round((grossTotal * Math.min(value, 100)) / 100);
      case "PRICE":
      default:
        return Math.min(grossTotal, value);
    }
  }, [isV2, v2.discountAmount, base.discount, selectedOption, base.addOnsTotal]);

  const discountPercentage = useMemo(() => {
    if (!base.discount || base.discount.type !== "PERCENTAGE") return undefined;
    return base.discount.value;
  }, [base.discount]);

  // Total guest count (seats) derived from current ticket selections.
  const totalGuestCount = useMemo(() => {
    if (isV2 && ticketSelections.length > 0) {
      return ticketSelections.reduce((sum, sel) => {
        const tt = selectedTicketTypesV2.find((t: any) => t.id === sel.ticketTypeId);
        const guestsPerTicket = tt?.numberOfGuestsPerTicket ?? 1;
        return sum + sel.quantity * guestsPerTicket;
      }, 0);
    }
    // Non-V2 calendar-visibility bookings are single-guest appointments.
    return 1;
  }, [isV2, ticketSelections, selectedTicketTypesV2]);

  // Visible steps
  const visibleSteps = useMemo(() => {
    const steps: CheckoutStep[] = ["calendar"];

    if (isV2 && selectedTicketTypesV2.length > 0) {
      steps.push("tickets");
    }

    if (selectedAddOns.length > 0) {
      steps.push("addons");
    }

    // System identity questions never render here (this flow keeps the personal
    // step); hide the userform step if it would have nothing to show.
    if (
      selectedCourse?.userQuestionForm?.isEnabled &&
      hasRenderableQuestions(
        selectedCourse.userQuestionForm.questions,
        totalGuestCount,
      )
    ) {
      steps.push("userform");
    }

    // Skip the personal step when the basket checkout will collect buyer info
    // downstream. Single-guest bookings always skip (the buyer is the only
    // guest); multi-guest bookings skip only when the listing has opted in
    // to prefilling other guests with buyer info — otherwise this flow still
    // collects each guest here.
    const skipPersonalStep =
      totalGuestCount === 1 ||
      !!selectedCourse?.allowPrefillOthersGuestsOnCheckout;

    if (!skipPersonalStep) {
      steps.push("personal");
    }

    steps.push("basketChoice");

    return steps;
  }, [selectedAddOns, selectedCourse?.userQuestionForm, selectedCourse?.allowPrefillOthersGuestsOnCheckout, isV2, selectedTicketTypesV2, totalGuestCount]);

  const currentStepIndex = visibleSteps.indexOf(step);
  const isFirstStep = currentStepIndex === 0;
  const isCheckoutPhase = step !== "calendar";

  const goToNextStep = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < visibleSteps.length) {
      const nextStep = visibleSteps[nextIndex]!;
      setStep(nextStep);
      if (nextStep !== "calendar" && step === "calendar") {
        onCheckoutStart?.();
      }
    }
  }, [currentStepIndex, visibleSteps, step, onCheckoutStart]);

  const goToPreviousStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      const prevStep = visibleSteps[prevIndex]!;
      setStep(prevStep);
      if (prevStep === "calendar" && step !== "calendar") {
        onCheckoutEnd?.();
      }
    }
  }, [currentStepIndex, visibleSteps, step, onCheckoutEnd]);

  const goToStep = useCallback(
    (targetStep: CheckoutStep) => {
      if (visibleSteps.includes(targetStep)) {
        const wasCalendar = step === "calendar";
        const goingToCalendar = targetStep === "calendar";
        setStep(targetStep);
        if (!wasCalendar && goingToCalendar) onCheckoutEnd?.();
        if (wasCalendar && !goingToCalendar) onCheckoutStart?.();
      }
    },
    [visibleSteps, step, onCheckoutStart, onCheckoutEnd],
  );

  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  // Guest validation
  const guestErrors = useMemo(() => {
    return base.personalDetails.guests.map((guest: any, index: number) =>
      validateGuest(
        {
          firstName: guest.firstName || "",
          lastName: guest.lastName || "",
          email: guest.email || "",
          phoneNumber: guest.phoneNumber || "",
        },
        { isFirstGuest: index === 0, requirePhone: true },
      ),
    );
  }, [base.personalDetails.guests]);

  const handleGuestBlur = useCallback((guestIndex: number, field: string) => {
    setGuestTouched((prev) => {
      const newTouched = [...prev];
      if (!newTouched[guestIndex]) newTouched[guestIndex] = {};
      newTouched[guestIndex] = { ...newTouched[guestIndex], [field]: true };
      return newTouched;
    });
  }, []);

  // These listings are fixed-duration appointments, so no ticket is selected
  // when slots are fetched — but a listing offering exactly one ticket type
  // answers the question unambiguously. Several, and we ask for no cap rather
  // than guess a pool.
  const capTicketTypeIdFor = useCallback(
    (listingId: string) =>
      appointmentCapTicketTypeId({
        // Read the listing, do not assert it. This surface admits any course
        // with isAppointmentScheduleEnabled — CalendarVisibility filters on
        // nothing else — so a variable-duration listing reaches here. Claiming
        // it is fixed-duration returns its sole ticket id, and the slots
        // endpoint flips into ticket-filtered generation on exactly that
        // combination (durationMode "variable" + a ticketTypeId), which can
        // render a listing with no availability at all where it previously
        // showed slots.
        isVariableDuration: isVariableDurationCourse(coursesData.get(listingId)),
        ticketTypeIds: (ticketTypesV2Map.get(listingId) ?? []).map(
          (t: any) => t.id,
        ),
      }),
    [ticketTypesV2Map, coursesData],
  );

  // Equipment left on the chosen slot, for the ticket selector's per-ticket cap.
  const poolMaxTicketsByTicketTypeId = useMemo(() => {
    if (!selectedListingId || selectedTime === null || !selectedDate) {
      return undefined;
    }
    const dates = availabilityMap.get(selectedListingId) || [];
    const selectedDateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;
    const dateData = dates.find(
      (d) => moment.tz(d.dateISO, timezone).format("YYYY-MM-DD") === selectedDateStr,
    );
    const matchingSlot = (dateData?.availableSlots ?? []).find(
      (s: any) => s.startMinutes === selectedTime,
    );
    return appointmentPoolCaps(
      capTicketTypeIdFor(selectedListingId),
      (matchingSlot as any)?.poolMaxTickets,
    );
  }, [
    selectedListingId,
    selectedTime,
    selectedDate,
    availabilityMap,
    timezone,
    capTicketTypeIdFor,
  ]);

  // Load available slots for ALL listings
  const loadSlots = useCallback(
    async (month: number, year: number) => {
      setIsLoadingSlots(true);
      try {
        const results = await Promise.all(
          listings.map(async (listing) => {
            const result = await onLoadAvailableSlots(
              listing.id,
              month,
              year,
              capTicketTypeIdFor(listing.id),
            );
            return [listing.id, result.dates || []] as const;
          }),
        );
        setAvailabilityMap(new Map(results));
      } catch {
        // Partial results are still useful
      } finally {
        setIsLoadingSlots(false);
      }
    },
    [listings, onLoadAvailableSlots],
  );

  // Search for next available date across all listings
  const searchNextAvailable = useCallback(
    async (startMonth: number, startYear: number) => {
      setIsSearchingNextAvailable(true);
      setNextAvailableDateInfo(null);

      let month = startMonth;
      let year = startYear;

      for (let i = 0; i < 6; i++) {
        try {
          const results = await Promise.all(
            listings.map(async (listing) => {
              const result = await onLoadAvailableSlots(
                listing.id,
                month,
                year,
                capTicketTypeIdFor(listing.id),
              );
              return result.dates || [];
            }),
          );

          for (const dates of results) {
            const availableDate = dates.find(
              (d: any) => !d.isDisabled && d.availableSlots?.length > 0,
            );
            if (availableDate) {
              setNextAvailableDateInfo({
                dateISO: availableDate.dateISO,
                month,
                year,
              });
              setIsSearchingNextAvailable(false);
              return;
            }
          }
        } catch {
          // continue
        }

        month++;
        if (month > 12) { month = 1; year++; }
      }
      setIsSearchingNextAvailable(false);
    },
    [listings, onLoadAvailableSlots],
  );

  const handleMonthChange = useCallback(
    (date: Date) => {
      setCurrentMonth(date);
      setSelectedDate(null);
      setSelectedTime(null);
      setSelectedListingId(null);
      loadSlots(date.getMonth() + 1, date.getFullYear());
    },
    [loadSlots],
  );

  // Initial load
  useEffect(() => {
    const now = new Date();
    loadSlots(now.getMonth() + 1, now.getFullYear());
    searchNextAvailable(now.getMonth() + 1, now.getFullYear());
  }, []);

  // Validation
  const canContinue = useCallback((): boolean => {
    switch (step) {
      case "calendar":
        return !!selectedDate && selectedTime !== null && !!selectedListingId;
      case "tickets": {
        const totalTickets = ticketSelections.reduce((sum, s) => sum + s.quantity, 0);
        if (totalTickets === 0) return false;
        const apptMin = selectedCourse?.appointmentScheduleSettings?.minTicketsPerOrder;
        const apptMax = selectedCourse?.appointmentScheduleSettings?.maxTicketsPerOrder;
        if (apptMin && totalTickets < apptMin) return false;
        if (apptMax && totalTickets > apptMax) return false;
        return v2.constraintErrors.length === 0;
      }
      case "addons":
        return true;
      case "userform":
        return base.isUserFormValid;
      case "personal": {
        const guest = base.personalDetails.guests[0];
        if (!guest) return false;
        const hasValidGuests = guestErrors.every((errors: any) => !hasGuestErrors(errors));
        const hasAcceptedTerms = mandatoryTickBox ? guest.mandatoryTickBox === true : true;
        return hasValidGuests && hasAcceptedTerms;
      }
      case "basketChoice":
        return true;
      default:
        return false;
    }
  }, [step, selectedDate, selectedTime, selectedListingId, base, mandatoryTickBox, guestErrors, ticketSelections, selectedCourse?.appointmentScheduleSettings, v2.constraintErrors]);

  const getValidationMessage = useCallback((): string | undefined => {
    switch (step) {
      case "calendar":
        if (!selectedDate) return "Please select a date";
        if (selectedTime === null) return "Please select a time slot";
        if (!selectedListingId) return "Please select a service";
        return undefined;
      case "tickets": {
        const totalTickets = ticketSelections.reduce((sum, s) => sum + s.quantity, 0);
        if (totalTickets === 0) return "Please select at least one ticket";
        if (v2.constraintErrors.length > 0) return v2.constraintErrors[0]?.message;
        return undefined;
      }
      case "userform":
        if (!base.isUserFormValid) return "Please complete all required fields";
        return undefined;
      case "personal": {
        const guest = base.personalDetails.guests[0];
        if (!guest) return "Please fill in your details";
        if (!guest.firstName.trim()) return "First name is required";
        if (!guest.lastName.trim()) return "Last name is required";
        if (!guest.email.trim()) return "Email is required";
        if (!guest.phoneNumber.trim()) return "Phone number is required";
        if (mandatoryTickBox && !guest.mandatoryTickBox)
          return "Please accept the terms and conditions";
        return undefined;
      }
      default:
        return undefined;
    }
  }, [step, selectedDate, selectedTime, selectedListingId, base, mandatoryTickBox]);


  // Build basket item data (shared between add-to-basket and proceed-to-checkout)
  const buildBasketItemData = useCallback(() => {
    if (selectedTime === null || !selectedDate || !selectedCourse) return null;
    if (!isV2 && !selectedOption) return null;

    const addOnItems = base.addOnSelections
      .filter((sel) => sel.quantity > 0)
      .map((sel) => {
        const addOn = selectedAddOns.find((a: any) => a.id === sel.addOnId);
        return { addOnId: sel.addOnId, addOnName: addOn?.name || "Add-on", quantity: sel.quantity, price: addOn?.price || 0 };
      });

    const courseTimezone = selectedCourse.timezone || timezone;
    const hours = Math.floor(selectedTime / 60);
    const minutes = selectedTime % 60;
    const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")} ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
    const sessionDate = moment.tz(dateStr, courseTimezone).toISOString();

    return {
      type: "booking" as const,
      bookingType: "appointment" as const,
      courseId: selectedCourse._id,
      courseName: selectedCourse.title,
      sessionDate,
      // Timezone only — NOT appointmentData.
      //
      // ⚠ This flow cannot know the duration. `pricingOptions` here is built
      // from `selectedTicketTypes`, which is the V1 `ticketTypes` map; on a
      // v2 listing that array is empty, so `selectedOption` is null and any
      // `?? 60` fallback stamps a fabricated hour on every V2 appointment.
      // An earlier version of this did exactly that. With no appointmentData
      // the basket shows the start alone, which is the honest answer for a
      // flow that does not know the end. Fix it by resolving the duration
      // from the V2 ticket map, not by guessing.
      timezone: courseTimezone,
      ticketSelections: isV2
        ? ticketSelections
            .filter((s) => s.quantity > 0)
            .map((sel) => {
              const tt = selectedTicketTypesV2.find((t: any) => t.id === sel.ticketTypeId);
              return {
                ticketTypeId: sel.ticketTypeId,
                ticketTypeName: tt?.name || "Ticket",
                quantity: sel.quantity,
                price: tt?.price || 0,
                numberOfGuestsPerTicket: 1,
              };
            })
        : [{
            ticketTypeId: selectedOption!.id,
            ticketTypeName: selectedOption!.name,
            quantity: 1,
            price: selectedOption!.price,
            numberOfGuestsPerTicket: selectedOption!.numberOfGuestsPerTicket,
            typeCharge: selectedOption!.typeCharge,
            bookingPolicy: selectedOption!.bookingPolicy,
          }],
      addOns: addOnItems.length > 0 ? addOnItems : undefined,
      userInputs: base.userFormValues?.length ? base.userFormValues : undefined,
      price: total,
      currency,
      imageUrl: selectedCourse.images?.[0]?.url,
      isOnline: selectedCourse.isOnline,
      guestDetails: {
        firstName: base.personalDetails.guests[0]?.firstName || "",
        lastName: base.personalDetails.guests[0]?.lastName || "",
        email: base.personalDetails.guests[0]?.email || "",
        phone: base.personalDetails.guests[0]?.phoneNumber || "",
      },
      checkoutGuestsInformation: base.personalDetails.guests.map((g: any) => ({
        firstName: g.firstName || "",
        lastName: g.lastName || "",
        email: g.email || "",
        phoneNumber: g.phoneNumber || "",
        marketingOptIn: g.marketingOptIn || false,
        mandatoryTickBox: g.mandatoryTickBox,
      })),
    };
  }, [selectedOption, selectedTime, selectedDate, base, selectedAddOns, selectedCourse, total, currency, timezone, isV2, ticketSelections, selectedTicketTypesV2]);

  const handleAddToBasket = useCallback(() => {
    if (!onAddToBasket) return;
    const itemData = buildBasketItemData();
    if (itemData) onAddToBasket(itemData);
  }, [onAddToBasket, buildBasketItemData]);

  const handleProceedToBasketCheckout = useCallback(() => {
    const itemData = buildBasketItemData();
    if (itemData) onProceedToBasketCheckout(itemData);
  }, [onProceedToBasketCheckout, buildBasketItemData]);

  // Format selected time for display
  const formattedTime = useMemo(() => {
    if (!selectedDate || selectedTime === null) return undefined;
    const dateStr = selectedDate.toLocaleDateString("en-GB", {
      weekday: "short", day: "numeric", month: "long", year: "numeric",
    });
    const hours = Math.floor(selectedTime / 60);
    const minutes = selectedTime % 60;
    const timeStr = `${hours % 12 || 12}:${String(minutes).padStart(2, "0")}${hours >= 12 ? "pm" : "am"}`;
    return `${dateStr} at ${timeStr}`;
  }, [selectedDate, selectedTime]);

  const ticketItems = useMemo(() => {
    if (isV2 && ticketSelections.length > 0) {
      return ticketSelections
        .filter((s) => s.quantity > 0)
        .map((sel) => {
          const tt = v2.ticketTypes.find((t) => t.id === sel.ticketTypeId);
          const pricingResult = v2.pricingResults.find((r) => r.ticketTypeId === sel.ticketTypeId);
          const lineTotal = pricingResult?.lineTotal;
          return {
            name: tt?.name ?? "Ticket",
            price: pricingResult?.result.finalPrice ?? tt?.price ?? 0,
            count: sel.quantity,
            lineTotal,
          };
        });
    }
    return selectedOption
      ? [{ name: selectedOption.name, price: selectedOption.price, count: 1 }]
      : [];
  }, [isV2, ticketSelections, v2.ticketTypes, v2.pricingResults, selectedOption]);

  const addOnItems = base.addOnSelections
    .map((sel) => {
      const addOn = selectedAddOns.find((a: any) => a.id === sel.addOnId);
      if (!addOn || sel.quantity === 0) return null;
      return { name: addOn.name, price: addOn.price, count: sel.quantity };
    })
    .filter(Boolean) as { name: string; price: number; count: number }[];

  // Calendar phase renders inline (no panel wrapper)
  if (step === "calendar") {
    return (
      <div className="w-full">
        <CalendarVisibilityStep
          listings={listings}
          availabilityMap={availabilityMap}
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          selectedListingId={selectedListingId}
          onDateChange={setSelectedDate}
          onTimeChange={setSelectedTime}
          onListingChange={setSelectedListingId}
          isLoading={isLoadingSlots}
          onMonthChange={handleMonthChange}
          currentMonth={currentMonth}
          timezone={timezone}
          currency={currency}
          nextAvailableDateInfo={nextAvailableDateInfo}
          isSearchingNextAvailable={isSearchingNextAvailable}
        />

        {/* Inline continue button */}
        <div className="mt-4">
          <button
            onClick={goToNextStep}
            disabled={!canContinue()}
            className="w-full py-3 px-6 rounded-lg font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: canContinue() ? "var(--color-primary, #03c2b9)" : undefined }}
          >
            Continue
          </button>
          {getValidationMessage() && (
            <p className="text-sm text-muted-foreground text-center mt-2">
              {getValidationMessage()}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Checkout phase renders in a panel (shown in overlay by parent)
  return (
    <>
      <CheckoutPanel>
        <CheckoutPanel.Header
          title={selectedCourse?.title || "Booking"}
          subtitle="Appointment Booking"
          onClose={() => {
            goToStep("calendar");
          }}
        />

        <CheckoutPanel.Content isEmbedMode={isEmbedMode}>
          <CheckoutPanel.Main>
            {/* Step navigation */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
              {visibleSteps.filter((s) => s !== "calendar").map((stepName, index) => (
                <React.Fragment key={stepName}>
                  {index > 0 && <span className="text-muted-foreground/70">/</span>}
                  <button
                    onClick={() => {
                      const stepIdx = visibleSteps.indexOf(stepName);
                      if (stepIdx < currentStepIndex) goToStep(stepName);
                    }}
                    disabled={visibleSteps.indexOf(stepName) > currentStepIndex}
                    className={`capitalize ${
                      stepName === step
                        ? "text-primary font-medium"
                        : visibleSteps.indexOf(stepName) < currentStepIndex
                          ? "text-muted-foreground hover:text-primary cursor-pointer"
                          : "text-muted-foreground/70"
                    }`}
                  >
                    {stepName}
                  </button>
                </React.Fragment>
              ))}
            </div>

            {step === "tickets" && isV2 && (
              <TicketSelectionStepV2
                ticketTypes={v2.ticketTypes}
                selections={ticketSelections}
                onSelectionChange={updateTicketSelection}
                currency={currency}
                totalAvailability={selectedCourse?.appointmentScheduleSettings?.maxTicketsPerOrder}
                constraintErrors={v2.constraintErrors}
                poolMaxTicketsByTicketTypeId={poolMaxTicketsByTicketTypeId}
              />
            )}

            {step === "addons" && (
              <AddOnsStep
                addOns={selectedAddOns}
                selections={base.addOnSelections}
                onSelectionChange={base.updateAddOnSelection}
                currency={currency}
              />
            )}

            {step === "userform" && (
              <UserFormStep
                questions={selectedCourse?.userQuestionForm?.questions || []}
                totalGuests={totalGuestCount}
                values={base.userFormValues}
                onChange={base.setUserFormValues}
                onValidationChange={base.setIsUserFormValid}
                mode={selectedCourse?.userQuestionForm?.mode || "all"}
              />
            )}

            {step === "personal" && (
              <PersonalDetailsStep
                values={{
                  firstName: base.personalDetails.guests[0]?.firstName || "",
                  lastName: base.personalDetails.guests[0]?.lastName || "",
                  email: base.personalDetails.guests[0]?.email || "",
                  phone: base.personalDetails.guests[0]?.phoneNumber || "",
                  marketingOptIn: base.personalDetails.guests[0]?.marketingOptIn || false,
                  mandatoryTickBox: base.personalDetails.guests[0]?.mandatoryTickBox || false,
                }}
                errors={{
                  firstName: guestErrors[0]?.firstName,
                  lastName: guestErrors[0]?.lastName,
                  email: guestErrors[0]?.email,
                  phone: guestErrors[0]?.phoneNumber,
                }}
                touched={{
                  firstName: guestTouched[0]?.firstName,
                  lastName: guestTouched[0]?.lastName,
                  email: guestTouched[0]?.email,
                  phone: guestTouched[0]?.phoneNumber,
                }}
                onChange={(field, value) => {
                  const fieldMap: Record<string, string> = {
                    firstName: "firstName",
                    lastName: "lastName",
                    email: "email",
                    phone: "phoneNumber",
                    marketingOptIn: "marketingOptIn",
                    mandatoryTickBox: "mandatoryTickBox",
                  };
                  base.updatePersonalDetails(0, fieldMap[field] as any, value);
                }}
                onBlur={(field) => {
                  const fieldMap: Record<string, string> = {
                    firstName: "firstName", lastName: "lastName",
                    email: "email", phone: "phoneNumber",
                  };
                  handleGuestBlur(0, fieldMap[field] || field);
                }}
                mandatoryTickBox={mandatoryTickBox}
                showMarketingOptIn={school?.isNewsletterSignupEnabled ?? true}
                businessName={school?.name || ""}
                customMarketingOptInLabel={school?.customNewsletterSignupText}
                schoolID={school?._id || ""}
                businessCountry={school?.businessCountry}
              />
            )}

            {step === "basketChoice" && (
              <BasketChoiceStep
                itemName={selectedCourse?.title || "Booking"}
                dateLabel={formattedTime}
                total={total}
                currency={currency}
                basketItemCount={basketItemCount}
                onAddToBasket={handleAddToBasket}
                onProceedToCheckout={handleProceedToBasketCheckout}
                directCheckout={directCheckout}
                basketConflict={basketConflict}
              />
            )}
          </CheckoutPanel.Main>

          <CheckoutPanel.Sidebar>
            <CheckoutSummaryCard
              title={selectedCourse?.title || "Booking"}
              thumbnail={selectedCourse?.images?.[0]?.url}
              description={formattedTime}
              isOnline={selectedCourse?.isOnline}
              items={[...ticketItems, ...addOnItems]}
              total={total}
              currency={currency}
              discount={discountAmount}
              discountPercentage={discountPercentage}
              cancellationPolicyUrl={cancellationPolicyUrl}
              termsSettings={termsSettings}
              customTerms={customTerms}
              timezone={timezone}
              onOpenTermsDialog={onOpenTermsDialog}
            />
          </CheckoutPanel.Sidebar>
        </CheckoutPanel.Content>
      </CheckoutPanel>

      <CheckoutFooter
        label="Time Selected"
        value={formattedTime}
        total={total}
        currency={currency}
        showBack={true}
        onBack={goToPreviousStep}
        showCancel={!!onClose}
        onCancel={onClose}
        showContinue={step !== "basketChoice"}
        onContinue={goToNextStep}
        continueText="Continue"
        continueDisabled={!canContinue()}
        validationMessage={getValidationMessage()}
        isEmbedMode={isEmbedMode}
      />
    </>
  );
}

export type { CheckoutStep as CalendarVisibilityCheckoutStep };
